import { useEffect, useState, useCallback } from "react";
import {
  agnesListMealPlans, agnesGenerateMealPlan, agnesDeleteMealPlan, agnesMealPlanToShopping,
  agnesGetTodayMeal, agnesCookTonight, agnesDigestSendNow, agnesSetDigestEmail,
} from "@/lib/api";
import { Overline } from "@/components/site/Primitives";
import { Sparkles, Printer, ShoppingBasket, Trash2, ChevronLeft, Utensils, MessageCircle, ChefHat, Mail, Send } from "lucide-react";
import { toast } from "sonner";

export const MealPlans = ({ setShopping, openShopping, onRefine, digestOptIn, setDigestOptIn }) => {
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState(null);
  const [prefs, setPrefs] = useState("");
  const [generating, setGenerating] = useState(false);
  const [todayMeal, setTodayMeal] = useState(null);
  const [cook, setCook] = useState(null);
  const [cooking, setCooking] = useState(false);

  const load = useCallback(async () => {
    try { setPlans(await agnesListMealPlans()); } catch {}
  }, []);
  useEffect(() => { load(); agnesGetTodayMeal().then(setTodayMeal).catch(() => {}); }, [load]);

  const cookTonight = async () => {
    setCooking(true);
    try {
      const res = await agnesCookTonight();
      setCook(res);
      setShopping(res.shopping_list);
      toast.success(res.added ? `${res.added} thing${res.added > 1 ? "s" : ""} for tonight added to your shopping list.` : "You've everything you need — how lovely.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Agnes couldn't gather the recipe just now.");
    } finally { setCooking(false); }
  };

  const toggleDigest = async () => {
    const next = !digestOptIn;
    setDigestOptIn(next);
    try { await agnesSetDigestEmail(next); toast.success(next ? "Agnes will send your Sunday digest." : "Sunday digest turned off."); }
    catch { setDigestOptIn(!next); toast.error("Could not update your preference."); }
  };
  const sendDigestNow = async () => {
    try {
      const res = await agnesDigestSendNow();
      toast.success(res.sent ? "Your digest is on its way." : "Email isn't switched on yet — ask the cottage to add a mail key.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send your digest.");
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const plan = await agnesGenerateMealPlan(prefs);
      setPlans((p) => [plan, ...p]);
      setActive(plan);
      setPrefs("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Agnes couldn't settle on a plan just now — do try again.");
    }
    finally { setGenerating(false); }
  };

  const remove = async (id, e) => {
    e?.stopPropagation();
    try { await agnesDeleteMealPlan(id); setPlans((p) => p.filter((x) => x.id !== id)); if (active?.id === id) setActive(null); } catch {}
  };

  const toShopping = async (id) => {
    try {
      const res = await agnesMealPlanToShopping(id);
      setShopping(res.shopping_list);
      toast.success(res.added ? `${res.added} item${res.added > 1 ? "s" : ""} added to your shopping list.` : "Those are already on your list.");
      openShopping && openShopping();
    } catch { toast.error("Could not add to your list."); }
  };

  const print = () => window.print();

  if (active) {
    return (
      <div className="h-full overflow-y-auto" data-testid="meal-plan-detail">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <button onClick={() => setActive(null)} data-testid="meal-plan-back" className="flex items-center gap-1 font-sans text-[0.7rem] uppercase tracking-[0.14em] text-brand-copper mb-6">
            <ChevronLeft size={15} /> All plans
          </button>
          <div className="print-area">
            <Overline>A week at the cottage</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl font-light mt-3 mb-8">{active.title}</h2>
            <div className="space-y-3 mb-10">
              {active.days.map((d, i) => (
                <div key={i} className="flex gap-4 border-b border-brand-linen pb-3" data-testid={`meal-day-${i}`}>
                  <span className="font-sans text-[0.7rem] uppercase tracking-[0.14em] text-brand-sage w-24 shrink-0 pt-1">{d.day}</span>
                  <div>
                    <p className="font-serif text-xl text-brand-charcoal leading-snug">{d.meal}</p>
                    {d.note && <p className="font-body text-brand-muted text-sm mt-0.5">{d.note}</p>}
                  </div>
                </div>
              ))}
            </div>
            {active.shopping?.length > 0 && (
              <div className="bg-brand-warm-white border border-brand-linen p-6">
                <h3 className="font-serif text-2xl mb-4 flex items-center gap-2"><ShoppingBasket size={20} className="text-brand-sage" /> Shopping list</h3>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5">
                  {active.shopping.map((s, i) => (
                    <li key={i} className="font-body text-brand-body flex items-center gap-2"><span className="text-brand-gold">·</span> {s}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3 mt-8 no-print">
            <button onClick={() => toShopping(active.id)} data-testid="meal-plan-to-shopping"
              className="inline-flex items-center gap-2 bg-brand-olive text-brand-cream px-6 py-3 font-sans text-xs uppercase tracking-[0.14em] hover:bg-brand-charcoal transition-colors">
              <ShoppingBasket size={16} /> Add to my shopping list
            </button>
            <button onClick={() => onRefine && onRefine(active)} data-testid="meal-plan-refine"
              className="inline-flex items-center gap-2 border border-brand-gold text-brand-charcoal px-6 py-3 font-sans text-xs uppercase tracking-[0.14em] hover:bg-brand-linen transition-colors">
              <MessageCircle size={16} /> Refine with Agnes
            </button>
            <button onClick={print} data-testid="meal-plan-print"
              className="inline-flex items-center gap-2 border border-brand-gold text-brand-charcoal px-6 py-3 font-sans text-xs uppercase tracking-[0.14em] hover:bg-brand-linen transition-colors">
              <Printer size={16} /> Print
            </button>
          </div>
        </div>
        <style>{`@media print { body * { visibility: hidden !important; } .print-area, .print-area * { visibility: visible !important; } .print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 24px; } .no-print { display: none !important; } }`}</style>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="meal-plans-view">
      <div className="max-w-3xl mx-auto px-6 py-8">
        {todayMeal?.meal && (
          <div className="bg-brand-olive text-brand-cream p-6 mb-6" data-testid="cook-tonight-card">
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <div>
                <span className="font-sans text-[0.6rem] uppercase tracking-[0.16em] text-brand-cream/70">Tonight · {todayMeal.day}</span>
                <h3 className="font-serif text-2xl sm:text-3xl mt-1 leading-snug">{todayMeal.meal}</h3>
              </div>
              <button onClick={cookTonight} disabled={cooking} data-testid="cook-tonight-btn"
                className="inline-flex items-center gap-2 bg-brand-cream text-brand-charcoal px-6 py-3 font-sans text-xs uppercase tracking-[0.14em] hover:bg-brand-warm-white transition-colors disabled:opacity-60">
                <ChefHat size={16} /> {cooking ? "Gathering…" : "Cook this tonight"}
              </button>
            </div>
            {cook && (
              <div className="mt-5 pt-5 border-t border-brand-cream/25" data-testid="cook-tonight-result">
                <p className="font-body text-brand-cream/90 leading-relaxed mb-4">{cook.method}</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
                  {cook.ingredients.map((ing, i) => (
                    <span key={i} className="font-body text-sm text-brand-cream/85">· {ing}</span>
                  ))}
                </div>
                {cook.missing?.length > 0 && (
                  <p className="font-sans text-[0.68rem] uppercase tracking-[0.12em] text-brand-cream/70 mt-4">
                    {cook.missing.length} item{cook.missing.length > 1 ? "s" : ""} added to your shopping list
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="bg-brand-warm-white border border-brand-linen p-8 mb-6">
          <Overline>Plan the week</Overline>
          <h2 className="font-serif text-3xl sm:text-4xl font-light mt-3 mb-3">A week of cottage dinners</h2>
          <p className="font-body text-brand-muted leading-relaxed mb-5">Let Agnes plan seven cosy dinners around your larder, with a tidy shopping list to match.</p>
          <input value={prefs} onChange={(e) => setPrefs(e.target.value)} data-testid="meal-plan-prefs"
            placeholder="Any wishes? (e.g. mostly vegetarian, quick weeknights)"
            className="w-full bg-brand-cream border border-brand-linen px-4 py-3 font-body mb-4 focus:outline-none focus:border-brand-gold" />
          <button onClick={generate} disabled={generating} data-testid="meal-plan-generate"
            className="inline-flex items-center gap-2 bg-brand-olive text-brand-cream px-7 py-3.5 font-sans text-xs uppercase tracking-[0.16em] hover:bg-brand-charcoal transition-colors disabled:opacity-60">
            <Sparkles size={16} /> {generating ? "Agnes is planning…" : "Plan my week"}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4 justify-between bg-brand-warm-white border border-brand-linen px-6 py-4 mb-10" data-testid="digest-controls">
          <label className="flex items-center gap-3 cursor-pointer">
            <button type="button" onClick={toggleDigest} role="switch" aria-checked={!!digestOptIn} data-testid="digest-toggle"
              className={`w-10 h-5 rounded-full transition-colors relative shrink-0 ${digestOptIn ? "bg-brand-sage" : "bg-brand-linen"}`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-brand-cream transition-all ${digestOptIn ? "left-[22px]" : "left-0.5"}`} />
            </button>
            <span className="font-body text-sm text-brand-body flex items-center gap-2"><Mail size={15} className="text-brand-sage" /> Email me a Sunday digest</span>
          </label>
          <button onClick={sendDigestNow} data-testid="digest-send-now"
            className="inline-flex items-center gap-2 border border-brand-gold text-brand-charcoal px-4 py-2 font-sans text-[0.68rem] uppercase tracking-[0.12em] hover:bg-brand-linen transition-colors">
            <Send size={14} /> Send me one now
          </button>
        </div>

        <span className="overline text-[0.6rem]">Your saved plans</span>
        {plans.length === 0 ? (
          <p className="font-body text-brand-muted italic py-8">No plans yet — let Agnes make your first one.</p>
        ) : (
          <ul className="mt-4 space-y-3" data-testid="meal-plan-list">
            {plans.map((p) => (
              <li key={p.id}>
                <button onClick={() => setActive(p)} data-testid={`meal-plan-card-${p.id}`}
                  className="w-full group flex items-center gap-4 text-left bg-brand-warm-white border border-brand-linen px-5 py-4 hover:border-brand-gold transition-colors">
                  <Utensils size={20} className="text-brand-sage shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-xl text-brand-charcoal truncate">{p.title}</p>
                    <p className="font-sans text-xs text-brand-muted">{p.days?.length || 0} dinners · {p.shopping?.length || 0} to buy</p>
                  </div>
                  <Trash2 size={16} onClick={(e) => remove(p.id, e)} className="opacity-0 group-hover:opacity-100 text-brand-muted hover:text-brand-copper shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
